import type {
  FormManagementExportDetailColumn,
  FormManagementExportDetailRow,
  FormManagementQuestionChart,
  FormManagementQuestionChartKind,
  FormManagementQuestionChartSegment,
  FormManagementWorkspaceContract,
  FormManagementWorkspaceData,
} from '@/contracts/form-management'
import type { FormFolderContract, FormUiContract } from '@/contracts/questionnaire'
import { createSyntheticMeta, isCustomerOnboardingForm } from '@/contracts/questionnaire'
import {
  ANALYTICS_SMALL_SAMPLE_THRESHOLD,
  type AnalyticsAnswerInput,
  type AnalyticsQuestionChart,
  type AnalyticsQuestionInput,
  type AnalyticsResponseInput,
  aggregateQuestionAnalytics,
  calculateResponseMetrics,
} from '@/lib/analytics-calculation'
import { primaryFormId, sampleQuestions } from '@/lib/mock-data'
import {
  TAIWAN_LANDLINE_EXTENSION_PRESET,
  TAIWAN_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_PRESET,
  TAIWAN_MOBILE_REGEX,
} from '@/lib/phone-validation'
import { buildPublicFormUrl } from '@/lib/public-form-url'
import { questionnaireSnapshotFormats } from '@/lib/questionnaire-snapshot'
import {
  TAIWAN_ADDRESS_MODE,
  TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
} from '@/lib/taiwan-address'
import { formatTaiwanLandlineAnswer } from '@/lib/taiwan-landline'
import {
  type Choice,
  type Question,
  type QuestionType,
  QuestionTypeSchema,
  isLayoutQuestionType,
} from '@/types/form'

function formatNumber(value: number) {
  return new Intl.NumberFormat('zh-TW').format(value)
}

const allQuestionTypes = QuestionTypeSchema.options as QuestionType[]

const chartColors = [
  '#38BDF8',
  '#22C55E',
  '#FACC15',
  '#FB923C',
  '#A78BFA',
  '#F472B6',
  '#2DD4BF',
  '#94A3B8',
]

const questionTypeLabels: Record<QuestionType, string> = {
  SHORT_TEXT: '短文字',
  LONG_TEXT: '長文字',
  MULTIPLE_CHOICE: '單選題',
  DROPDOWN: '下拉選單',
  CHECKBOXES: '多選題',
  PICTURE_CHOICE: '圖片選擇',
  YES_NO: '是非題',
  OPINION_SCALE: '意見量表',
  RATING: '評分題',
  NPS: 'NPS',
  DATE: '日期',
  TIME: '時間',
  PHONE_NUMBER: '電話',
  TAIWAN_MOBILE: '台灣手機',
  TAIWAN_LANDLINE: '台灣市話',
  TAIWAN_ADDRESS: '台灣地址',
  EMAIL: 'Email',
  WEBSITE: '網址',
  NUMBER: '數字',
  NUMBER_SLIDER: '數字滑桿',
  TOTAL: '總計',
  NESTED_SELECT: '階層選單',
  FILE_UPLOAD: '檔案上傳',
  SIGNATURE: '簽名',
  RANKING: '排序題',
  MATRIX: '矩陣題',
  STATEMENT: '說明文字',
  SECTION_TITLE: '段落標題',
  INTRO_BLOCK: '開始區塊',
  DIVIDER: '分隔線',
}

const syntheticQuestionTitles: Record<QuestionType, string> = {
  SHORT_TEXT: '請留下您的姓名',
  LONG_TEXT: '如果可以改善一件事，您希望是什麼？',
  MULTIPLE_CHOICE: '您最常使用哪一種問卷用途？',
  DROPDOWN: '預計採購區間',
  CHECKBOXES: '目前需要哪些協助？',
  PICTURE_CHOICE: '偏好的問卷封面風格',
  YES_NO: '是否需要業務聯繫？',
  OPINION_SCALE: '整體使用體驗分數',
  RATING: '請為服務流程評分',
  NPS: '您有多願意推薦 MiaoForm？',
  DATE: '預計導入日期',
  TIME: '方便聯繫時間',
  PHONE_NUMBER: '聯絡電話',
  TAIWAN_MOBILE: '手機',
  TAIWAN_LANDLINE: '公司市話',
  TAIWAN_ADDRESS: '公司地址',
  EMAIL: '聯絡 Email',
  WEBSITE: '公司網站',
  NUMBER: '預估使用人數',
  NUMBER_SLIDER: '導入急迫程度',
  TOTAL: '導入規模計算預覽',
  NESTED_SELECT: '主要服務區域',
  FILE_UPLOAD: '請上傳需求附件',
  SIGNATURE: '確認簽名',
  RANKING: '請排序您最重視的改善項目',
  MATRIX: '各流程的重要程度',
  STATEMENT: '資料使用說明',
  SECTION_TITLE: '客戶基本資料',
  INTRO_BLOCK: '開始頁說明',
  DIVIDER: '分段線',
}

const syntheticChoices: Partial<Record<QuestionType, string[]>> = {
  MULTIPLE_CHOICE: ['客戶回饋', '活動報名', '員工調查', 'B2B 建檔'],
  DROPDOWN: ['10 萬以下', '10-50 萬', '50-100 萬', '100 萬以上'],
  CHECKBOXES: ['報表分析', '權限審核', 'CRM / ERP 串接', '通知流程'],
  PICTURE_CHOICE: ['清爽商務', '產品展示', '活動視覺', '品牌主色'],
  YES_NO: ['是', '否'],
  NESTED_SELECT: ['北區 / 台北', '北區 / 桃園', '中區 / 台中', '南區 / 高雄'],
  RANKING: ['報表分析', '權限與審核流程', 'CRM / ERP 串接', '通知流程'],
}

const exportDetailSyntheticQuestions: Question[] = [
  {
    id: 'q-company-landline',
    formId: sampleQuestions[0]?.formId ?? primaryFormId,
    type: 'TAIWAN_LANDLINE',
    title: '公司市話',
    description: '明細匯出使用的 mock-safe 台灣市話欄位。',
    required: false,
    settings: {
      placeholder: '2345-6789',
      validation: 'phone',
      taiwanLandlineAreaCode: '02',
    },
    choices: [],
    orderIndex: sampleQuestions.length,
  },
]

function getExportDetailQuestions() {
  if (sampleQuestions.some((question) => question.id === 'q-company-landline'))
    return sampleQuestions
  return [...sampleQuestions, ...exportDetailSyntheticQuestions]
}

const textTokens = [
  '自動化',
  '報表',
  '審核',
  '客戶資料',
  'ERP',
  '提醒',
  '權限',
  '匯出',
  '主管',
  '流程',
]

export function getFormManagementWorkspaceContract({
  form,
  folders,
  canManageForms,
}: {
  form: FormUiContract
  folders: FormFolderContract[]
  canManageForms: boolean
}): FormManagementWorkspaceContract {
  const visibleTabs = [
    'analytics',
    'export',
    'completionPage',
    'operations',
  ] as FormManagementWorkspaceContract['visibleTabs']

  return {
    form,
    folders,
    capabilities: {
      canEdit: canManageForms,
      canShare: true,
      canExport: canManageForms,
      canManageOperations: canManageForms,
      canViewAnalytics: true,
      canManageCustomerWorkflow: canManageForms && isCustomerOnboardingForm(form),
    },
    defaultTab: 'completionPage',
    visibleTabs,
    sourceMeta: createSyntheticMeta('2026-05-22'),
  }
}

export function getFormManagementWorkspaceData(form: FormUiContract): FormManagementWorkspaceData {
  const sourceMeta = createSyntheticMeta('2026-05-22')
  const analyticsResponses = buildSyntheticAnalyticsResponses(form.responseCount)
  const responseMetrics = calculateResponseMetrics(analyticsResponses)
  const views = Math.max(420, form.responseCount * 12 + 280)
  const starts = responseMetrics.starts
  const completions = responseMetrics.completions
  const averageSeconds = 146

  return {
    sourceMeta,
    panelStates: {
      analytics: form.responseCount > 0 ? 'ready' : 'empty',
      export: 'provider_not_configured',
      crossAnalysis: 'ready',
      visitorActivity: 'provider_not_configured',
      linkGenerator: 'provider_not_configured',
      notifications: 'provider_not_configured',
      completionPage: 'ready',
      customerWorkflow: isCustomerOnboardingForm(form) ? 'ready' : 'empty',
      operations: 'ready',
    },
    metrics: [
      {
        id: 'views',
        label: '瀏覽量',
        value: formatNumber(views),
        helper: 'synthetic visitor sessions',
        trendLabel: '較上期 +8.4%',
        tone: 'sky',
      },
      {
        id: 'starts',
        label: '開始數',
        value: formatNumber(starts),
        helper: '進入第一題的人數',
        trendLabel: '較上期 +5.1%',
        tone: 'indigo',
      },
      {
        id: 'completions',
        label: '完成數',
        value: formatNumber(completions),
        helper: '完成送出的回應',
        trendLabel: '較上期 +3.6%',
        tone: 'emerald',
      },
      {
        id: 'completion_rate',
        label: '完成率',
        value: `${responseMetrics.completionRate}%`,
        helper: 'analytics helper：完成數 / 開始數',
        trendLabel: '較上期 +1.2%',
        tone: 'violet',
      },
      {
        id: 'average_time',
        label: '平均完成時間',
        value: '2:26',
        helper: `${averageSeconds} 秒`,
        trendLabel: '較上期 -9 秒',
        tone: 'amber',
      },
    ],
    trend: [
      { label: '05/16', views: 82, starts: 54, completions: 33 },
      { label: '05/17', views: 96, starts: 68, completions: 42 },
      { label: '05/18', views: 121, starts: 85, completions: 51 },
      { label: '05/19', views: 144, starts: 103, completions: 64 },
      { label: '05/20', views: 132, starts: 94, completions: 58 },
      { label: '05/21', views: 168, starts: 124, completions: 71 },
      { label: '05/22', views: 153, starts: 111, completions: Math.max(24, completions) },
    ],
    dropoffRows: [
      {
        id: 'drop-company',
        question: '公司基本資料',
        reached: starts,
        dropoffs: Math.max(4, Math.round(starts * 0.06)),
        dropoffRate: 6,
      },
      {
        id: 'drop-contact',
        question: '主要聯絡人',
        reached: Math.round(starts * 0.9),
        dropoffs: Math.max(6, Math.round(starts * 0.11)),
        dropoffRate: 11,
      },
      {
        id: 'drop-needs',
        question: '需求與預算',
        reached: Math.round(starts * 0.76),
        dropoffs: Math.max(9, Math.round(starts * 0.16)),
        dropoffRate: 16,
      },
      {
        id: 'drop-review',
        question: '業務補充資訊',
        reached: Math.round(starts * 0.62),
        dropoffs: Math.max(5, Math.round(starts * 0.09)),
        dropoffRate: 9,
      },
    ],
    questionStats: [
      {
        id: 'qs-company',
        question: '公司產業類別',
        typeLabel: '單選題',
        responses: completions,
        topResult: '製造業 34%',
        completionRate: Math.min(100, form.completionRate + 8),
      },
      {
        id: 'qs-budget',
        question: '預估採購金額',
        typeLabel: '數字欄位',
        responses: Math.max(0, completions - 3),
        topResult: '平均 186,000',
        completionRate: Math.max(0, form.completionRate - 3),
      },
      {
        id: 'qs-need',
        question: '主要需求摘要',
        typeLabel: '長文字',
        responses: Math.max(0, completions - 5),
        topResult: '自動化、報表、審核',
        completionRate: Math.max(0, form.completionRate - 7),
      },
    ],
    questionCharts: completions > 0 ? buildQuestionCharts(form, analyticsResponses) : [],
    recentActivities: [
      {
        id: 'activity-link',
        label: '問卷連結已複製',
        description: '管理工作區以 synthetic public URL 顯示，尚未建立真 tracking backend。',
        timeLabel: '剛剛',
      },
      {
        id: 'activity-export',
        label: '資料匯出 contract 已建立',
        description:
          '匯出頁展示 route、query、欄位與權限需求，並只產生 browser-local synthetic 模擬檔。',
        timeLabel: '12 分鐘前',
      },
      {
        id: 'activity-provider',
        label: 'Mock-safe contract preview',
        description: 'Email 與 Webhook 仍停在 mock-safe 狀態。',
        timeLabel: '今天',
      },
    ],
    providerStatuses: [
      {
        id: 'analytics',
        label: 'Analytics aggregation',
        state: 'mock_only',
        description: '目前顯示 synthetic metrics，尚未寫入 visitor/session event table。',
      },
      {
        id: 'export',
        label: 'Export job queue',
        state: 'not_configured',
        description:
          '第一版僅開 CSV / JSON browser-local 明細匯出；R2 job、排程、下載 storage 留待下一批。',
      },
      {
        id: 'notifications',
        label: 'Email / Webhook',
        state: 'not_configured',
        description: '通知與 Webhook 只展示 contract preview，不送出請求。',
      },
    ],
    exportFormats: [
      {
        id: 'csv',
        label: 'CSV',
        description: '以逗號分隔欄位建立明細檔，第一版每次最多 1000 筆。',
        enabled: true,
      },
      {
        id: 'json',
        label: 'JSON',
        description: '保留欄位 key 與答案值，適合系統匯入或資料結構檢查。',
        enabled: true,
      },
    ],
    exportDetailColumns: buildExportDetailColumns(),
    exportDetailRows: buildExportDetailRows(form.id),
    exportDetailPageSize: 5,
    exportFields: [
      { id: 'submitted_at', label: '提交時間', group: 'metadata', selected: true },
      { id: 'status', label: '完成狀態', group: 'metadata', selected: true },
      { id: 'source', label: '來源 / UTM', group: 'hiddenFields', selected: true },
      { id: 'answers', label: '所有題目答案', group: 'answers', selected: true },
      {
        id: 'customer_company',
        label: '公司欄位',
        group: 'customer',
        selected: isCustomerOnboardingForm(form),
      },
      {
        id: 'customer_contact',
        label: '聯絡人欄位',
        group: 'customer',
        selected: isCustomerOnboardingForm(form),
      },
    ],
    exportFilters: [
      { id: 'date_range', label: '日期範圍', value: '最近 30 天' },
      { id: 'completion_status', label: '完成狀態', value: '完整與部分完成' },
      { id: 'source', label: '來源', value: '全部來源' },
      { id: 'sales_code', label: '業務代碼', value: '全部業務' },
    ],
    exportJobPreview: {
      route: `/api/forms/${form.id}/responses/export`,
      method: 'GET',
      query:
        'format=csv&limit=1000&status=complete&include=metadata,answers,hiddenFields,customerFields&mode=contract-preview',
      estimatedRows: completions,
      permissionRequired: 'forms:export',
      providerState: 'provider_not_configured',
    },
    snapshotPreview: {
      id: `snapshot-preview-${form.id}`,
      title: isCustomerOnboardingForm(form) ? '建檔申請單筆資料快照' : '問卷單筆資料快照',
      description:
        '展示未來單筆問卷資料快照需要的欄位、格式、權限與 audit 要求；目前只做 mock-safe preview。',
      surface: 'form_management_workspace',
      recommendedSurface: isCustomerOnboardingForm(form)
        ? 'customer_application_detail'
        : 'form_management_workspace',
      formId: form.id,
      formTitle: form.title,
      responseId: 'future-response-id',
      sourceRecordId: isCustomerOnboardingForm(form) ? 'future-source-record-id' : undefined,
      customerName: isCustomerOnboardingForm(form) ? '範例客戶公司' : undefined,
      submittedAt: '2026-05-22 10:24',
      generatedAt: new Date().toISOString(),
      providerState: 'provider_not_configured',
      permissionRequired: isCustomerOnboardingForm(form)
        ? 'customers:snapshot:read'
        : 'forms:snapshot:read',
      auditRequirement:
        'production 產生或下載單筆資料快照時必須寫入 audit_logs，包含 actor、form、response、格式與用途。',
      warning:
        '目前只是 contract preview，不產生真實 PDF、Excel、CSV 或外部下載連結；舊填答紀錄列表不會被恢復。',
      formats: questionnaireSnapshotFormats,
      metadata: [
        {
          label: '推薦入口',
          value: isCustomerOnboardingForm(form) ? '客戶詳情 / 問卷回應' : '問卷管理工作區',
        },
        {
          label: '權限需求',
          value: isCustomerOnboardingForm(form) ? '客戶資料快照檢視' : '問卷資料快照檢視',
        },
        { label: 'Provider', value: 'provider_not_configured' },
      ],
      fields: [
        {
          id: 'submittedAt',
          label: '提交時間',
          value: '2026-05-22 10:24',
          sourceStateLabel: '問卷 metadata',
        },
        {
          id: 'owner',
          label: '提交者 / 業務',
          value: isCustomerOnboardingForm(form) ? '內部業務' : '匿名填答者',
          sourceStateLabel: '問卷 metadata',
        },
        ...(isCustomerOnboardingForm(form)
          ? [
              {
                id: 'companyName',
                label: '公司名稱',
                value: '範例客戶公司',
                sourceStateLabel: '問卷帶入',
                sourceQuestionTitle: '公司名稱',
              },
              {
                id: 'contactEmail',
                label: '聯絡人 Email',
                value: 'buyer@example.com',
                sourceStateLabel: '問卷帶入',
                sourceQuestionTitle: '主要聯絡人 Email',
              },
            ]
          : []),
      ],
      answers: [
        {
          id: 'snapshot-answer-company',
          questionTitle: isCustomerOnboardingForm(form) ? '公司名稱' : '第一題',
          questionTypeLabel: '短文字',
          answer: isCustomerOnboardingForm(form) ? '範例客戶公司' : '範例答案',
          mappedFieldLabel: isCustomerOnboardingForm(form) ? '公司資料 / 公司名稱' : undefined,
          sourceStateLabel: '問卷帶入',
        },
        {
          id: 'snapshot-answer-need',
          questionTitle: isCustomerOnboardingForm(form) ? '需求摘要' : '回饋內容',
          questionTypeLabel: '長文字',
          answer: isCustomerOnboardingForm(form)
            ? '希望將問卷資料轉為客戶主檔，供 ERP 建檔前確認。'
            : '這是單筆資料快照的 mock-safe 預覽內容。',
          mappedFieldLabel: isCustomerOnboardingForm(form) ? '業務欄位 / 需求摘要' : undefined,
          sourceStateLabel: '問卷帶入',
        },
      ],
      sourceMeta,
    },
    crossAnalysisQuestions: [
      {
        id: 'industry',
        label: '公司產業類別',
        typeLabel: '單選題',
        supported: true,
      },
      {
        id: 'budget',
        label: '預估採購金額區間',
        typeLabel: '下拉選單',
        supported: true,
      },
      {
        id: 'source',
        label: '來源活動',
        typeLabel: '隱藏欄位',
        supported: true,
      },
      {
        id: 'needs_text',
        label: '主要需求摘要',
        typeLabel: '長文字',
        supported: false,
        disabledReason: '開放文字題需先完成關鍵詞聚合後才能交叉分析。',
      },
    ],
    crossAnalysis: {
      xQuestionId: 'industry',
      yQuestionId: 'budget',
      sampleSize: completions,
      rows: ['製造業', '科技業', '批發零售', '專業服務'],
      columns: ['10 萬以下', '10-50 萬', '50-100 萬', '100 萬以上'],
      cells: [
        { row: '製造業', column: '10 萬以下', count: 4, percentage: 9 },
        { row: '製造業', column: '10-50 萬', count: 12, percentage: 26 },
        { row: '製造業', column: '50-100 萬', count: 8, percentage: 17 },
        { row: '製造業', column: '100 萬以上', count: 5, percentage: 11 },
        { row: '科技業', column: '10 萬以下', count: 2, percentage: 4 },
        { row: '科技業', column: '10-50 萬', count: 7, percentage: 15 },
        { row: '科技業', column: '50-100 萬', count: 6, percentage: 13 },
        { row: '科技業', column: '100 萬以上', count: 3, percentage: 7 },
        { row: '批發零售', column: '10 萬以下', count: 5, percentage: 11 },
        { row: '批發零售', column: '10-50 萬', count: 8, percentage: 17 },
        { row: '批發零售', column: '50-100 萬', count: 3, percentage: 7 },
        { row: '批發零售', column: '100 萬以上', count: 1, percentage: 2 },
        { row: '專業服務', column: '10 萬以下', count: 3, percentage: 7 },
        { row: '專業服務', column: '10-50 萬', count: 9, percentage: 20 },
        { row: '專業服務', column: '50-100 萬', count: 4, percentage: 9 },
        { row: '專業服務', column: '100 萬以上', count: 2, percentage: 4 },
      ],
      insights: [
        '製造業在 10-50 萬區間最集中，適合優先安排業務追蹤。',
        '科技業高預算比例較高，但樣本數仍是 synthetic preview。',
        '開放文字題目前不參與交叉分析，避免誤判未聚合資料。',
      ],
    },
    visitorSessions: [
      {
        id: 'visit-001',
        sourceLabel: 'Email 活動',
        utmSource: 'newsletter',
        campaign: 'may-onboarding',
        device: 'Mobile',
        browser: 'Safari',
        startedAt: '2026-05-22 09:12',
        completedAt: '2026-05-22 09:15',
        status: 'completed',
        hiddenFields: [
          { label: 'salesCode', value: 'S-104' },
          { label: 'campaignCode', value: 'MAY-A' },
        ],
      },
      {
        id: 'visit-002',
        sourceLabel: '業務分享',
        utmSource: 'sales',
        campaign: 'north-team',
        device: 'Desktop',
        browser: 'Chrome',
        startedAt: '2026-05-22 10:04',
        status: 'started',
        hiddenFields: [
          { label: 'salesCode', value: 'S-087' },
          { label: 'territory', value: 'north' },
        ],
      },
      {
        id: 'visit-003',
        sourceLabel: '網站嵌入',
        utmSource: 'website',
        campaign: 'pricing-page',
        device: 'Tablet',
        browser: 'Edge',
        startedAt: '2026-05-21 16:30',
        status: 'abandoned',
        hiddenFields: [
          { label: 'embed', value: 'pricing' },
          { label: 'plan', value: 'teams' },
        ],
      },
      {
        id: 'visit-004',
        sourceLabel: 'LinkedIn',
        utmSource: 'social',
        campaign: 'b2b-intake',
        device: 'Desktop',
        browser: 'Firefox',
        startedAt: '2026-05-21 14:22',
        completedAt: '2026-05-21 14:27',
        status: 'completed',
        hiddenFields: [
          { label: 'salesCode', value: 'S-116' },
          { label: 'campaignCode', value: 'SOC-B2B' },
        ],
      },
    ],
    linkGenerator: {
      templateColumns: [
        'name',
        'email',
        'salesCode',
        'campaignCode',
        'utm_source',
        'utm_medium',
        'utm_campaign',
      ],
      uploadState: 'provider_not_configured',
      previewUrl: buildPublicFormUrl(form.id, {
        searchParams: {
          utm_source: 'newsletter',
          utm_campaign: 'may-onboarding',
          salesCode: 'S-104',
          campaignCode: 'MAY-A',
        },
      }),
      parameters: [
        {
          id: 'utm_source',
          label: 'utm_source',
          value: 'newsletter',
          description: '流量來源，例如 email、sales、social。',
        },
        {
          id: 'utm_campaign',
          label: 'utm_campaign',
          value: 'may-onboarding',
          description: '活動代碼，供後續分析歸因。',
        },
        {
          id: 'salesCode',
          label: 'salesCode',
          value: 'S-104',
          description: '業務代碼，B2B 建檔流程可用來帶入負責業務。',
        },
        {
          id: 'campaignCode',
          label: 'campaignCode',
          value: 'MAY-A',
          description: '內部活動批號，會寫入 hidden fields contract。',
        },
      ],
      generatedLinks: [
        {
          id: 'link-001',
          name: '北區業務五月名單',
          url: buildPublicFormUrl(form.id, {
            searchParams: {
              utm_source: 'sales',
              utm_campaign: 'north-team',
              salesCode: 'S-087',
            },
          }),
          count: 32,
          status: 'mock_only',
          createdAt: '2026-05-22 09:30',
        },
        {
          id: 'link-002',
          name: 'Email onboarding 批次 A',
          url: buildPublicFormUrl(form.id, {
            searchParams: {
              utm_source: 'newsletter',
              utm_campaign: 'may-onboarding',
              campaignCode: 'MAY-A',
            },
          }),
          count: 128,
          status: 'provider_not_configured',
          createdAt: '2026-05-21 18:10',
        },
      ],
    },
    notificationRules: [
      {
        id: 'notify-owner',
        name: '問卷送出通知',
        channel: 'email',
        eventLabel: '每次完成送出時',
        recipients: ['owner@miaoform.example', 'ops@miaoform.example'],
        enabled: true,
        providerState: 'provider_not_configured',
        templateSubject: `收到新的 ${form.title} 回覆`,
        templatePreview:
          '您好，MiaoForm 已收到新的問卷送出。此 preview 不寄送真 Email，只展示未來通知 payload。',
      },
      {
        id: 'notify-manager',
        name: '主管審核通知',
        channel: 'manager_email',
        eventLabel: 'B2B 建檔申請送出時',
        recipients: ['manager-from-team-scope'],
        enabled: isCustomerOnboardingForm(form),
        providerState: 'provider_not_configured',
        templateSubject: '有新的客戶建檔申請待審核',
        templatePreview:
          '主管會收到申請摘要、提交業務、重複客戶訊號與審核連結。實際寄送需等 Email provider 審查。',
      },
      {
        id: 'notify-webhook',
        name: 'Webhook 推送',
        channel: 'webhook',
        eventLabel: '完成 / 審核狀態變更時',
        recipients: ['https://example.invalid/webhook/miaoform'],
        enabled: false,
        providerState: 'provider_not_configured',
        templateSubject: 'Webhook payload preview',
        templatePreview:
          '{"event":"form.completed","source":"synthetic","mode":"contract-preview","signature":"not-generated"}',
      },
    ],
    notificationTestPreview: {
      route: `/api/forms/${form.id}/notifications/test`,
      method: 'POST',
      payloadPreview:
        '{"event":"form.completed","recipient":"owner@miaoform.example","mode":"contract-preview"}',
      providerState: 'provider_not_configured',
    },
    completionPage: {
      title: form.thankYouScreen.title,
      description: form.thankYouScreen.description,
      animationStyle: form.thankYouScreen.animationStyle ?? 'check_confetti',
      ctas: [
        ...(form.thankYouScreen.showPrimaryCta === true
          ? [
              {
                id: 'primary',
                label: form.thankYouScreen.primaryCtaLabel ?? '回到官網',
                url: form.thankYouScreen.primaryCtaUrl ?? 'https://miaoform.example',
                style: 'primary' as const,
              },
            ]
          : []),
        ...(form.thankYouScreen.showSecondaryCta === true
          ? [
              {
                id: 'secondary',
                label: form.thankYouScreen.secondaryCtaLabel ?? '查看更多範例',
                url: form.thankYouScreen.secondaryCtaUrl ?? 'https://miaoform.example/templates',
                style: 'secondary' as const,
              },
            ]
          : []),
      ],
      redirectUrl: form.thankYouScreen.redirectUrl,
      redirectDelaySeconds: form.thankYouScreen.redirectDelaySeconds,
      allowResubmit: Boolean(form.settings.allowResubmit),
      showResubmitButton: form.thankYouScreen.showResubmitButton === true,
      showShareButtons: form.thankYouScreen.showShareButtons === true,
      showNewSubmissionButton: form.thankYouScreen.showNewSubmissionButton !== false,
      newSubmissionButtonLabel: form.thankYouScreen.newSubmissionButtonLabel ?? '再次填寫新問卷',
    },
    customerWorkflow: {
      enabled: isCustomerOnboardingForm(form),
      requiresManagerReview: form.settings.requiresManagerReview !== false,
      autoCreateCustomer: form.settings.autoCreateCustomer === true,
      erpFilingStatus: isCustomerOnboardingForm(form) ? 'not_filed' : 'not_configured',
      applicationsPending: isCustomerOnboardingForm(form)
        ? Math.max(1, Math.round(completions * 0.35))
        : 0,
      customersCreated: isCustomerOnboardingForm(form)
        ? Math.max(0, Math.round(completions * 0.5))
        : 0,
      fieldMappings: [
        {
          id: 'map-company-full-name',
          sourceQuestion: '統編抬頭（公司全名）',
          targetField: '客戶來源與基本資料 / company_full_name',
          group: 'company',
          required: false,
        },
        {
          id: 'map-tax-id',
          sourceQuestion: '統一編號',
          targetField: '聯絡資訊 / tax_id',
          group: 'company',
          required: false,
        },
        {
          id: 'map-contact',
          sourceQuestion: '聯絡人',
          targetField: '聯絡資訊 / contact_name',
          group: 'contact',
          required: true,
        },
        {
          id: 'map-email',
          sourceQuestion: 'E-Mail',
          targetField: '聯絡資訊 / email',
          group: 'contact',
          required: false,
        },
        {
          id: 'map-department',
          sourceQuestion: '部門別',
          targetField: '部門與業務歸屬 / department',
          group: 'sales',
          required: true,
        },
        {
          id: 'map-region',
          sourceQuestion: '客戶區域別',
          targetField: '區域資訊 / region',
          group: 'region',
          required: true,
        },
      ],
      reviewSteps: [
        {
          id: 'step-submit',
          label: '業務送出建檔申請',
          owner: '業務',
          state: isCustomerOnboardingForm(form) ? 'ready' : 'not_configured',
        },
        {
          id: 'step-review',
          label: '主管審核與重複客戶確認',
          owner: '業務主管',
          state: isCustomerOnboardingForm(form) ? 'mock_only' : 'not_configured',
        },
        {
          id: 'step-create',
          label: '核准後建立客戶資料',
          owner: '系統 mock workflow',
          state: isCustomerOnboardingForm(form) ? 'mock_only' : 'not_configured',
        },
        {
          id: 'step-erp',
          label: '外部 ERP 手動建檔',
          owner: '業務 / 業助',
          state: 'not_configured',
        },
      ],
    },
    operationActions: [
      {
        id: 'archive',
        label: form.status === 'archived' ? '還原封存' : '封存問卷',
        description:
          form.status === 'archived'
            ? '將此問卷還原為草稿狀態；仍需另行切換公開狀態才可填寫。'
            : '將問卷狀態改為封存；封存後不能透過公開填寫路由填答。',
        enabled: true,
        tone: 'warning',
        confirmationTitle: form.status === 'archived' ? '還原這份問卷？' : '封存這份問卷？',
        confirmationDescription:
          form.status === 'archived'
            ? '還原後會回到草稿狀態，可再由清單或編輯器切換為公開。'
            : '封存後此問卷無法填寫，並可從封存狀態篩選查看。',
      },
    ],
  }
}

function buildExportDetailColumns(): FormManagementExportDetailColumn[] {
  return [
    { id: 'recordNo', label: '編號', group: 'metadata' },
    { id: 'submittedAt', label: '送出時間', group: 'metadata' },
    { id: 'status', label: '狀態', group: 'metadata' },
    { id: 'respondent', label: '填寫者', group: 'metadata' },
    { id: 'source', label: '來源', group: 'hiddenFields' },
    { id: 'customerName', label: '公司', group: 'customer' },
    { id: 'contactEmail', label: 'E-mail', group: 'customer' },
    { id: 'contactPhone', label: '電話', group: 'customer' },
    { id: 'timeSpent', label: '填寫時間', group: 'metadata' },
    ...getExportDetailQuestions().map((question) => ({
      id: `answer:${question.id}`,
      label: question.title,
      group: 'answers' as const,
      ...(question.settings.exportValueType === 'text' || question.settings.preserveLeadingZeros
        ? { spreadsheetFormat: 'text' as const }
        : {}),
    })),
  ]
}

function buildExportDetailRows(_formId: string): FormManagementExportDetailRow[] {
  return []
}

function buildQuestionCharts(
  form: FormUiContract,
  analyticsResponses: AnalyticsResponseInput[],
): FormManagementQuestionChart[] {
  const sampleQuestionByType = new Map<QuestionType, Question>(
    sampleQuestions.map((question) => [question.type, question]),
  )
  const completeResponses = analyticsResponses.filter((response) => response.status === 'complete')
  const answerableResponseIds = completeResponses
    .filter((response) => response.id.startsWith('response-complete-'))
    .map((response) => response.id)

  return allQuestionTypes.map((questionType, index) => {
    const question =
      sampleQuestionByType.get(questionType) ??
      createSyntheticQuestion(form.id, questionType, index)
    const analyticsQuestion = toAnalyticsQuestion(question)
    const analyticsChart = aggregateQuestionAnalytics({
      question: analyticsQuestion,
      responses: completeResponses,
      answers: buildSyntheticAnalyticsAnswers(analyticsQuestion, answerableResponseIds),
      smallSampleThreshold: ANALYTICS_SMALL_SAMPLE_THRESHOLD,
    })

    return {
      id: `chart-${questionType.toLowerCase()}`,
      questionId: question.id,
      questionTitle: question.title,
      questionType,
      typeLabel: questionTypeLabels[questionType],
      chartKind: analyticsChart.chartKind,
      totalAnswers: analyticsChart.totalAnswers,
      completionRate: analyticsChart.completionRate,
      skipped: analyticsChart.skipped,
      invalid: analyticsChart.invalid,
      summary: analyticsChart.summary,
      segments: withChartColors(analyticsChart.segments, index),
      stacks: analyticsChart.stacks?.map((stack, stackIndex) => ({
        ...stack,
        segments: withChartColors(stack.segments, index + stackIndex),
      })),
      tokens: buildAnalyticsTextTokens(questionType, analyticsChart),
      samples: analyticsChart.samples,
      numericSummary: analyticsChart.numericSummary,
      fileSummary: buildAnalyticsFileSummary(questionType, analyticsChart, index),
      suppression: analyticsChart.suppression,
    }
  })
}

function buildSyntheticAnalyticsResponses(completions: number): AnalyticsResponseInput[] {
  if (completions <= 0) return []
  const partials = 12

  return [
    ...Array.from({ length: completions }, (_, index) => ({
      id: `response-complete-${index + 1}`,
      status: 'complete' as const,
      submittedAt: `2026-05-${String(16 + (index % 12)).padStart(2, '0')}T02:00:00Z`,
    })),
    ...Array.from({ length: partials }, (_, index) => ({
      id: `response-partial-${index + 1}`,
      status: 'partial' as const,
      startedAt: `2026-05-${String(16 + (index % 12)).padStart(2, '0')}T01:00:00Z`,
    })),
    {
      id: 'response-preview-excluded',
      status: 'complete' as const,
      submittedAt: '2026-05-31T01:00:00Z',
      metadata: { preview: true },
    },
    {
      id: 'response-test-excluded',
      status: 'complete' as const,
      submittedAt: '2026-05-31T01:00:00Z',
      metadata: { source: 'test' },
    },
  ]
}

function toAnalyticsQuestion(question: Question): AnalyticsQuestionInput {
  return {
    id: question.id,
    title: question.title,
    type: question.type,
    choices: question.choices.map((choice) => ({
      label: choice.label,
      value: choice.value,
    })),
    settings: question.settings,
    fieldKey:
      typeof question.settings.dbFieldName === 'string' ? question.settings.dbFieldName : undefined,
  }
}

function buildSyntheticAnalyticsAnswers(
  question: AnalyticsQuestionInput,
  responseIds: string[],
): AnalyticsAnswerInput[] {
  if (isLayoutQuestionType(question.type)) return []

  return responseIds.map((responseId, index) => ({
    responseId,
    questionId: question.id,
    value: getSyntheticAnalyticsAnswerValue(question, index),
  }))
}

function getSyntheticAnalyticsAnswerValue(question: AnalyticsQuestionInput, index: number) {
  const choices = question.choices ?? []
  const choiceValues = choices.map((choice) => choice.value)

  if (
    ['MULTIPLE_CHOICE', 'DROPDOWN', 'PICTURE_CHOICE', 'YES_NO', 'NESTED_SELECT'].includes(
      question.type,
    )
  ) {
    return choiceValues[index % Math.max(choiceValues.length, 1)] ?? `option-${index % 3}`
  }

  if (question.type === 'CHECKBOXES') {
    return [
      choiceValues[index % Math.max(choiceValues.length, 1)] ?? 'option-a',
      choiceValues[(index + 1) % Math.max(choiceValues.length, 1)] ?? 'option-b',
    ]
  }

  if (question.type === 'NPS') return [10, 9, 8, 6, 7][index % 5]
  if (question.type === 'OPINION_SCALE' || question.type === 'RATING') return (index % 5) + 1
  if (question.type === 'NUMBER_SLIDER') return (index % 10) + 1
  if (question.type === 'NUMBER') return 120 + index * 8
  if (question.type === 'TOTAL') return 60 + index * 3
  if (question.type === 'DATE') return `2026-06-${String((index % 20) + 1).padStart(2, '0')}`
  if (question.type === 'TIME') return ['09:00', '13:30', '16:00', '18:30'][index % 4]
  if (question.type === 'EMAIL')
    return `${['buyer', 'ops', 'sales', 'service'][index % 4]}@example.com`
  if (question.type === 'PHONE_NUMBER') return ['0223456789', '0423456789', '0798765432'][index % 3]
  if (question.type === 'TAIWAN_MOBILE')
    return ['0912345678', '0988396960', '0933123456'][index % 3]
  if (question.type === 'TAIWAN_LANDLINE')
    return ['02-2345-6789#123', '04-2345-6789', '07-8899-1100'][index % 3]

  if (question.type === 'TAIWAN_ADDRESS') {
    return [
      {
        city: '臺北市',
        district: '內湖區',
        postalCode: '114',
        detail: '瑞光路 513 巷 26 號 8 樓',
      },
      {
        city: '臺中市',
        district: '西屯區',
        postalCode: '407',
        detail: '工業一路 88 號',
      },
      {
        city: '苗栗縣',
        district: '苗栗市',
        postalCode: '360',
        detail: '中正路 100 號',
      },
    ][index % 3]
  }

  if (question.type === 'MATRIX') {
    const rows = getStringSettingListFromAnalytics(question, 'rows', ['建立客戶資料', '主管審核'])
    const columns = getStringSettingListFromAnalytics(question, 'columns', [
      '不重要',
      '普通',
      '重要',
    ])
    return Object.fromEntries(
      rows.map((row, rowIndex) => [row, columns[(index + rowIndex) % columns.length]]),
    )
  }

  if (question.type === 'RANKING') {
    if (choiceValues.length === 0) return ['option-a', 'option-b', 'option-c']
    return [
      ...choiceValues.slice(index % choiceValues.length),
      ...choiceValues.slice(0, index % choiceValues.length),
    ]
  }

  if (question.type === 'WEBSITE')
    return ['https://example.com', 'https://case4.example'][index % 2]
  if (question.type === 'FILE_UPLOAD') return `需求附件-${index + 1}.pdf`
  if (question.type === 'SIGNATURE') return `signature-${index + 1}.png`
  if (question.type === 'LONG_TEXT') return '希望問卷資料能整理成客戶主檔草稿。'
  return ['林佳蓉', '陳怡安', '王柏仁'][index % 3]
}

function getStringSettingListFromAnalytics(
  question: AnalyticsQuestionInput,
  key: 'rows' | 'columns',
  fallback: string[],
) {
  const value = question.settings?.[key]
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : fallback
}

function withChartColors(
  segments: FormManagementQuestionChartSegment[],
  seed: number,
): FormManagementQuestionChartSegment[] {
  return segments.map((segment, index) => ({
    ...segment,
    color: segment.color ?? chartColors[(seed + index) % chartColors.length],
  }))
}

function buildAnalyticsTextTokens(
  questionType: QuestionType,
  chart: AnalyticsQuestionChart,
): FormManagementQuestionChart['tokens'] {
  if (questionType !== 'SHORT_TEXT' && questionType !== 'LONG_TEXT') return undefined

  return textTokens.map((label, tokenIndex) => ({
    id: `${questionType}-token-${tokenIndex}`,
    label,
    value: Math.max(1, chart.totalAnswers - tokenIndex),
    weight: Math.max(1, 6 - (tokenIndex % 6)),
  }))
}

function buildAnalyticsFileSummary(
  questionType: QuestionType,
  chart: AnalyticsQuestionChart,
  index: number,
): FormManagementQuestionChart['fileSummary'] {
  if (questionType !== 'FILE_UPLOAD' && questionType !== 'SIGNATURE') return undefined

  const fileTypes = withChartColors(
    questionType === 'SIGNATURE'
      ? [{ id: 'signature-png', label: 'PNG 簽名', value: chart.totalAnswers, percent: 100 }]
      : [{ id: 'file-pdf', label: 'PDF', value: chart.totalAnswers, percent: 100 }],
    index,
  )
  const statuses = withChartColors(
    [{ id: 'file-uploaded', label: '已上傳', value: chart.totalAnswers, percent: 100 }],
    index + 1,
  )

  return {
    totalFiles: chart.totalAnswers,
    averageFilesPerAnswer: chart.totalAnswers > 0 ? 1 : 0,
    totalSizeMb: chart.totalAnswers * (questionType === 'SIGNATURE' ? 1.2 : 3.8),
    fileTypes,
    statuses,
  }
}

function buildQuestionChartPayload({
  chartKind,
  completions,
  index,
  question,
  questionType,
  skipped,
  starts,
  totalAnswers,
}: {
  chartKind: FormManagementQuestionChartKind
  completions: number
  index: number
  question: Question
  questionType: QuestionType
  skipped: number
  starts: number
  totalAnswers: number
}): Pick<
  FormManagementQuestionChart,
  'fileSummary' | 'numericSummary' | 'samples' | 'segments' | 'stacks' | 'summary' | 'tokens'
> {
  if (chartKind === 'layout_block') {
    return {
      summary: '版面區塊不產生答案，只保留曝光與到達脈絡。',
      segments: [],
      samples: [
        {
          id: `${questionType}-layout-reached`,
          label: '到達人次',
          value: formatNumber(Math.max(starts - index * 8, completions)),
          helper: 'Synthetic preview',
        },
        {
          id: `${questionType}-layout-skip`,
          label: '可回答欄位',
          value: '0',
          helper: '不列入答案統計',
        },
      ],
    }
  }

  if (chartKind === 'numeric_summary') {
    const unit = questionType === 'NUMBER' ? '人' : questionType === 'TOTAL' ? '點' : '分'
    const numericSummary = {
      min: questionType === 'NUMBER' ? 12 : 18,
      max: questionType === 'NUMBER' ? 2200 : 186,
      average: questionType === 'NUMBER' ? 486 : 128,
      median: questionType === 'NUMBER' ? 360 : 120,
      unit,
    }

    return {
      summary: `平均 ${formatNumber(numericSummary.average)}${unit}，中位數 ${formatNumber(
        numericSummary.median,
      )}${unit}。`,
      numericSummary,
      segments: makeSegments(['低區間', '中低區間', '中高區間', '高區間'], totalAnswers, index),
    }
  }

  if (chartKind === 'text_cloud') {
    return {
      summary:
        questionType === 'LONG_TEXT'
          ? '開放回覆以關鍵詞雲與代表答案呈現。'
          : '短文字以常見詞與樣本答案呈現，避免誤讀為可量化選項。',
      segments: [],
      tokens: textTokens.map((label, tokenIndex) => ({
        id: `${questionType}-token-${tokenIndex}`,
        label,
        value: Math.max(3, totalAnswers - tokenIndex * 2),
        weight: Math.max(1, 6 - (tokenIndex % 6)),
      })),
      samples: [
        {
          id: `${questionType}-sample-1`,
          label: '代表回答',
          value: questionType === 'LONG_TEXT' ? '需要更快產生摘要報表與主管審核流程。' : '林佳蓉',
        },
        {
          id: `${questionType}-sample-2`,
          label: '代表回答',
          value: questionType === 'LONG_TEXT' ? '希望問卷資料能整理成客戶主檔草稿。' : '陳怡安',
        },
      ],
    }
  }

  if (chartKind === 'sample_list') {
    return {
      summary: '格式型答案以樣本清單與有效率呈現，不做外部驗證或寄送。',
      segments: makeSegments(['格式有效', '待人工確認', '未填寫'], totalAnswers + skipped, index),
      samples: getFormatSamples(questionType),
    }
  }

  if (chartKind === 'file_summary') {
    const fileTypes = makeSegments(
      questionType === 'SIGNATURE'
        ? ['PNG 簽名', 'SVG 簽名']
        : ['PDF', 'PNG / JPG', 'DOCX', '其他'],
      Math.max(totalAnswers + 8, 12),
      index,
    )
    const statuses = makeSegments(
      ['已上傳', '待補件', '格式不符'],
      totalAnswers + skipped,
      index + 2,
    )
    const totalFiles = fileTypes.reduce((sum, segment) => sum + segment.value, 0)

    return {
      summary:
        questionType === 'SIGNATURE'
          ? '簽名以檔案型摘要呈現，可檢視完成與待補件狀態。'
          : '檔案上傳以類型、數量、容量與處理狀態呈現。',
      segments: fileTypes,
      fileSummary: {
        totalFiles,
        averageFilesPerAnswer: Number((totalFiles / Math.max(totalAnswers, 1)).toFixed(1)),
        totalSizeMb: totalFiles * (questionType === 'SIGNATURE' ? 1.2 : 3.8),
        fileTypes,
        statuses,
      },
    }
  }

  if (chartKind === 'timeline') {
    return {
      summary: questionType === 'DATE' ? '日期答案依時間區間分布。' : '時間答案依時段分布。',
      segments: makeSegments(
        questionType === 'DATE'
          ? ['本週', '下週', '本月', '下月', '未定']
          : ['09:00-12:00', '13:00-15:00', '15:00-18:00', '18:00 後'],
        totalAnswers,
        index,
      ),
    }
  }

  if (chartKind === 'ranking') {
    const labels = getQuestionChoiceLabels(question, questionType)
    return {
      summary: '以平均名次與第一名比例呈現排序結果。',
      segments: labels.map((label, optionIndex) => {
        const averageRank = Number((1.4 + optionIndex * 0.55).toFixed(1))
        return {
          id: `${questionType}-rank-${optionIndex}`,
          label,
          value: Math.max(1, Math.round(totalAnswers * Math.max(0.18, 0.44 - optionIndex * 0.07))),
          percent: Math.max(8, Math.round(44 - optionIndex * 7)),
          color: chartColors[optionIndex % chartColors.length],
          helper: `平均第 ${averageRank} 名`,
        }
      }),
    }
  }

  if (chartKind === 'matrix') {
    const rows = getStringSettingList(question, 'rows', ['建立客戶資料', '主管審核', 'ERP 建檔'])
    const columns = getStringSettingList(question, 'columns', ['不重要', '普通', '重要'])

    return {
      summary: `${rows.length} 列 x ${columns.length} 欄的矩陣列統計。`,
      segments: [],
      stacks: rows.map((row, rowIndex) => ({
        id: `${questionType}-matrix-${rowIndex}`,
        label: row,
        total: totalAnswers,
        segments: makeSegments(columns, totalAnswers, index + rowIndex),
      })),
    }
  }

  if (chartKind === 'stacked_bar') {
    const labels = getQuestionChoiceLabels(question, questionType)
    return {
      summary: '多選題以每個選項的被選取率與複選重疊呈現。',
      segments: makeSegments(labels, totalAnswers * 2, index),
      stacks: [
        {
          id: `${questionType}-stack-selected`,
          label: '選取狀態',
          total: totalAnswers,
          segments: makeSegments(['選取 1 項', '選取 2 項', '選取 3 項以上'], totalAnswers, index),
        },
      ],
    }
  }

  if (chartKind === 'histogram') {
    const labels = getHistogramLabels(questionType)
    return {
      summary:
        questionType === 'NPS'
          ? 'NPS 以推薦者、被動者與批評者區間呈現。'
          : '分數型題目以分數區間和平均傾向呈現。',
      segments: makeSegments(labels, totalAnswers, index),
      numericSummary: {
        min: questionType === 'NPS' ? 0 : 1,
        max: questionType === 'NPS' ? 10 : 5,
        average: questionType === 'NPS' ? 8.4 : 4.2,
        median: questionType === 'NPS' ? 9 : 4,
        unit: questionType === 'RATING' ? '星' : '分',
      },
    }
  }

  if (chartKind === 'horizontal_bar') {
    const labels = getQuestionChoiceLabels(question, questionType)
    return {
      summary: '選項題以水平長條呈現排序後的選擇分布。',
      segments: makeSegments(labels, totalAnswers, index),
    }
  }

  return {
    summary: '單選類題型以圓環分佈呈現各選項占比。',
    segments: makeSegments(getQuestionChoiceLabels(question, questionType), totalAnswers, index),
  }
}

function createSyntheticQuestion(
  formId: string,
  questionType: QuestionType,
  orderIndex: number,
): Question {
  const id = `synthetic-chart-question-${questionType.toLowerCase()}`
  const choiceLabels = syntheticChoices[questionType] ?? []

  return {
    id,
    formId,
    type: questionType,
    title: syntheticQuestionTitles[questionType],
    required: !isLayoutQuestionType(questionType),
    settings: getSyntheticSettings(questionType),
    choices: choiceLabels.map((label, index) => createChoice(id, label, index)),
    orderIndex,
  }
}

function createChoice(questionId: string, label: string, orderIndex: number): Choice {
  return {
    id: `${questionId}-choice-${orderIndex}`,
    questionId,
    label,
    value: label.toLocaleLowerCase().replace(/\s+/g, '-'),
    orderIndex,
  }
}

function getSyntheticSettings(questionType: QuestionType): Question['settings'] {
  if (questionType === 'MATRIX') {
    return {
      rows: ['建立客戶資料', '主管審核', 'ERP 建檔'],
      columns: ['不重要', '普通', '重要'],
      selectionMode: 'single',
    }
  }

  if (questionType === 'NUMBER_SLIDER') {
    return { sliderMin: 1, sliderMax: 10, sliderStep: 1, numberSuffix: '分' }
  }

  if (questionType === 'TAIWAN_LANDLINE') {
    return {
      placeholder: '2345-6789',
      validation: 'phone',
      validationPreset: TAIWAN_LANDLINE_EXTENSION_PRESET,
      validationRegex: TAIWAN_LANDLINE_EXTENSION_REGEX,
      validationTestValue: '02-2345-6789#123',
      taiwanLandlineAreaCode: '02',
    }
  }

  if (questionType === 'TAIWAN_MOBILE') {
    return {
      placeholder: '0912345678',
      validation: 'phone',
      validationPreset: TAIWAN_MOBILE_PRESET,
      validationRegex: TAIWAN_MOBILE_REGEX,
      validationTestValue: '0912345678',
    }
  }

  if (questionType === 'TAIWAN_ADDRESS') {
    return {
      addressMode: TAIWAN_ADDRESS_MODE,
      postalCodePrecision: TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
      detailPlaceholder: '請輸入路名、段、巷弄、門牌、樓層',
    }
  }

  if (questionType === 'NUMBER') return { numberUnit: '人', numberUseThousands: true }
  if (questionType === 'TOTAL') return { totalUnit: '點', totalMode: 'sum' }

  return {}
}

function getQuestionChartKind(questionType: QuestionType): FormManagementQuestionChartKind {
  if (isLayoutQuestionType(questionType)) return 'layout_block'

  if (
    ['MULTIPLE_CHOICE', 'DROPDOWN', 'PICTURE_CHOICE', 'YES_NO', 'NESTED_SELECT'].includes(
      questionType,
    )
  ) {
    return 'donut'
  }

  if (questionType === 'CHECKBOXES') return 'stacked_bar'
  if (questionType === 'RANKING') return 'ranking'
  if (questionType === 'MATRIX') return 'matrix'
  if (questionType === 'FILE_UPLOAD' || questionType === 'SIGNATURE') return 'file_summary'
  if (questionType === 'DATE' || questionType === 'TIME') return 'timeline'
  if (questionType === 'NUMBER' || questionType === 'TOTAL') return 'numeric_summary'

  if (['OPINION_SCALE', 'RATING', 'NPS', 'NUMBER_SLIDER'].includes(questionType)) {
    return 'histogram'
  }

  if (questionType === 'SHORT_TEXT' || questionType === 'LONG_TEXT') return 'text_cloud'
  if (
    [
      'EMAIL',
      'PHONE_NUMBER',
      'TAIWAN_MOBILE',
      'TAIWAN_LANDLINE',
      'TAIWAN_ADDRESS',
      'WEBSITE',
    ].includes(questionType)
  ) {
    return 'sample_list'
  }

  return 'horizontal_bar'
}

function getQuestionChoiceLabels(question: Question, questionType: QuestionType) {
  if (question.choices.length > 0) return question.choices.map((choice) => choice.label)
  return syntheticChoices[questionType] ?? ['選項 A', '選項 B', '選項 C']
}

function getHistogramLabels(questionType: QuestionType) {
  if (questionType === 'NPS') return ['0-6 批評者', '7-8 被動者', '9-10 推薦者']
  if (questionType === 'NUMBER_SLIDER') return ['1-2', '3-4', '5-6', '7-8', '9-10']
  if (questionType === 'RATING') return ['1 星', '2 星', '3 星', '4 星', '5 星']
  return ['1', '2', '3', '4', '5']
}

function getStringSettingList(
  question: Question,
  key: 'rows' | 'columns',
  fallback: string[],
): string[] {
  const value = question.settings[key]
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : fallback
}

function makeSegments(
  labels: string[],
  total: number,
  seed: number,
): FormManagementQuestionChartSegment[] {
  if (labels.length === 0) return []
  const safeTotal = Math.max(total, labels.length)
  const weights = labels.map((_, index) =>
    Math.max(1, labels.length - index + ((seed + index) % 3)),
  )
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0)
  let remaining = safeTotal

  return labels.map((label, index) => {
    const isLast = index === labels.length - 1
    const value = isLast
      ? remaining
      : Math.max(1, Math.round((safeTotal * weights[index]) / weightTotal))
    remaining = Math.max(0, remaining - value)

    return {
      id: `${label}-${index}`,
      label,
      value,
      percent: Math.round((value / safeTotal) * 100),
      color: chartColors[index % chartColors.length],
    }
  })
}

function getFormatSamples(questionType: QuestionType) {
  if (questionType === 'EMAIL') {
    return [
      { id: 'email-sample-1', label: '最近回答', value: 'buyer@example.com' },
      { id: 'email-sample-2', label: '最近回答', value: 'ops-team@example.com' },
      { id: 'email-sample-3', label: '待確認', value: 'sales-contact@example.com' },
    ]
  }

  if (questionType === 'PHONE_NUMBER') {
    return [
      { id: 'phone-sample-1', label: '最近回答', value: '+886 2 7711 2299' },
      { id: 'phone-sample-2', label: '最近回答', value: '+886 4 1234 5678' },
      { id: 'phone-sample-3', label: '待確認', value: '+886 912 345 678' },
    ]
  }

  if (questionType === 'TAIWAN_MOBILE') {
    return [
      { id: 'mobile-sample-1', label: '最近回答', value: '0912-345-678' },
      { id: 'mobile-sample-2', label: '最近回答', value: '0988 396 960' },
      { id: 'mobile-sample-3', label: '待確認', value: '0933123456' },
    ]
  }

  if (questionType === 'TAIWAN_LANDLINE') {
    return [
      {
        id: 'landline-sample-1',
        label: '最近回答',
        value: formatTaiwanLandlineAnswer('+886 2 7711 2299'),
      },
      {
        id: 'landline-sample-2',
        label: '最近回答',
        value: formatTaiwanLandlineAnswer('+886 4 1234 5678'),
      },
      {
        id: 'landline-sample-3',
        label: '待確認',
        value: formatTaiwanLandlineAnswer('+886 7 8899 1100'),
      },
    ]
  }

  if (questionType === 'TAIWAN_ADDRESS') {
    return [
      { id: 'address-sample-1', label: '最近回答', value: '10000 臺北市中正區 重慶南路一段 1 號' },
      { id: 'address-sample-2', label: '最近回答', value: '40700 臺中市西屯區 工業一路 88 號' },
      { id: 'address-sample-3', label: '待確認', value: '36000 苗栗縣苗栗市 中正路 100 號' },
    ]
  }

  return [
    { id: 'website-sample-1', label: '最近回答', value: 'https://example.com' },
    { id: 'website-sample-2', label: '最近回答', value: 'https://case4.example' },
    { id: 'website-sample-3', label: '待確認', value: 'https://partner.example' },
  ]
}
